import { Button, Card } from "@peer-start/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { fetchLoginStatus } from "../api/client.js";

export const loginQueryKey = ["login-status"] as const;

export function LoginGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const status = useQuery({
    queryKey: loginQueryKey,
    queryFn: fetchLoginStatus,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  useEffect(() => {
    const expired = () => {
      void client.invalidateQueries({ queryKey: loginQueryKey });
    };
    window.addEventListener("peer-start-login-required", expired);
    return () => window.removeEventListener("peer-start-login-required", expired);
  }, [client]);

  if (status.data && (!status.data.enabled || status.data.authenticated)) return children;
  return (
    <main className="grid min-h-screen place-items-center bg-background p-6 text-foreground">
      <Card className="w-full max-w-lg">
        <p className="text-sm font-medium uppercase tracking-wide text-primary">{t("common.appName")}</p>
        <h1 className="mt-2 text-3xl font-medium">{t("login.title")}</h1>
        <p className="mt-3 text-muted-foreground">{t("login.description")}</p>
        {status.isPending ? (
          <p className="mt-5" role="status">
            {t("common.loading")}
          </p>
        ) : status.isError ? (
          <div className="mt-5">
            <p role="alert">{t("common.error")}</p>
            <Button className="mt-3" controlId="login.retry" onClick={() => void status.refetch()}>
              {t("common.retry")}
            </Button>
          </div>
        ) : (
          <>
            {new URLSearchParams(window.location.search).has("authError") ? (
              <p className="mt-4 text-destructive" role="alert">
                {t("login.failed")}
              </p>
            ) : null}
            <a
              className="mt-6 inline-flex min-h-10 items-center rounded-md bg-primary px-4 font-medium text-primary-foreground focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring"
              href="/api/auth/login"
              data-control-id="login.start"
            >
              {t("login.signIn")}
            </a>
            <p className="mt-4 text-sm text-muted-foreground">{t("login.sharedDemo")}</p>
          </>
        )}
      </Card>
    </main>
  );
}
